// LocalAdmissionsCard — the P2 differentiation piece. UK-centric
// aggregators show a single uni fee + a UCAS link. For the markets
// WUA goes deep on (UAE, Singapore, Hong Kong, China), applicants
// actually need to see three things that UK aggregators never cover:
//
//   1. Fee tiers — Emirati vs international at NUS, SG citizen vs PR
//      vs international, HK local (JUPAS) vs non-local, Chinese vs
//      Sino-foreign JV.
//   2. Scholarship corridors — Mubadala for UAE nationals, A*STAR +
//      ASEAN UG for Singapore, HKU/CUHK entrance for HK, CSC + muni
//      awards for China.
//   3. Entry routes — the five realistic applicant profiles
//      (A-Level, IB, AP, Tawjihi, Gaokao) each with its route into
//      this market's unis.
//
// Mounts only on uni pages for the covered markets (via
// `tagForCountry` indirectly — we query `/api/local-admissions/{cc}`
// and let it 404 to gracefully hide the card for other countries).
import { useEffect, useState } from "react";
import {
  Coins,
  Award,
  GraduationCap,
  ExternalLink,
  BadgeCheck,
  BookOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// country name/code → the 2-letter market code we serve local-admissions
// data for. Keeps the lookup logic in one place so the uni-page
// `uni.country` can be stored inconsistently ("UK" vs "United Arab
// Emirates" vs "Hong Kong") without needing to be normalised upstream.
const SUPPORTED = {
  ae: "AE", "united arab emirates": "AE", uae: "AE",
  sg: "SG", singapore: "SG",
  hk: "HK", "hong kong": "HK", "hong kong sar": "HK",
  cn: "CN", china: "CN", "mainland china": "CN",
};

function resolveMarketCode(country) {
  if (!country) return null;
  return SUPPORTED[String(country).toLowerCase().trim()] || null;
}

function formatUSD(n) {
  if (typeof n !== "number") return "—";
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `$${n}`;
}

export default function LocalAdmissionsCard({ country }) {
  const [data, setData] = useState(null);
  const cc = resolveMarketCode(country);
  useEffect(() => {
    if (!cc) { setData(null); return; }
    let cancelled = false;
    api.get(`/local-admissions/${cc.toLowerCase()}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => setData(null));
    return () => { cancelled = true; };
  }, [cc]);

  if (!cc || !data) return null;

  const tiers = data.fee_tiers || [];
  const scholarships = data.scholarship_corridors || [];
  const routes = data.entry_routes || [];
  const sources = data.sources || [];

  return (
    <Card
      data-testid="local-admissions-card"
      data-market={cc}
      className="mt-6 p-6 border-stone-200"
    >
      <div className="flex items-center gap-2">
        <BadgeCheck className="w-4 h-4 text-[#4C1D95]" />
        <div className="label-uppercase text-[#4C1D95]">
          Local-market depth · {data.market_name}
        </div>
      </div>
      <h3 className="mt-2 font-editorial text-xl text-stone-900 leading-tight">
        What applicants from different backgrounds actually need to know
      </h3>
      <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-3xl">
        The UK-centric picture misses most of this. These are the fee
        tiers, scholarship corridors and entry routes that determine
        whether you can afford and get into unis in this market.
      </p>

      {/* Fee tiers */}
      {tiers.length > 0 && (
        <div className="mt-6">
          <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
            <Coins className="w-3 h-3" /> Fee tiers
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {tiers.map((t) => (
              <div
                key={t.tier}
                data-testid={`local-admissions-tier-${t.tier}`}
                className="rounded-lg border border-stone-200 bg-stone-50 p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-stone-900 font-medium">{t.label}</div>
                  <div className="text-sm font-semibold text-stone-900">
                    {formatUSD(t.annual_usd)}<span className="text-xs text-stone-500">/yr</span>
                  </div>
                </div>
                {t.note && (
                  <div className="mt-1 text-xs text-stone-600 leading-snug">{t.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scholarships */}
      {scholarships.length > 0 && (
        <div className="mt-6">
          <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
            <Award className="w-3 h-3" /> Scholarship corridors
          </div>
          <div className="space-y-2">
            {scholarships.map((s) => (
              <div
                key={s.name}
                data-testid={`local-admissions-scholarship-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-lg border border-stone-200 bg-white p-3 flex items-start justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-stone-900">{s.name}</div>
                    {s.citizens_only && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-900 px-1.5 py-0">
                        citizens only
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-stone-500">
                    {s.from_country} → {s.to_country}
                  </div>
                  {s.note && (
                    <div className="mt-1 text-xs text-stone-600 leading-snug">{s.note}</div>
                  )}
                </div>
                {s.url && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 text-xs text-[#4C1D95] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" /> details
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entry routes */}
      {routes.length > 0 && (
        <div className="mt-6">
          <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" /> Entry routes by background
          </div>
          <div className="space-y-2">
            {routes.map((r, i) => (
              <div
                key={i}
                data-testid={`local-admissions-route-${i}`}
                className="rounded-lg border border-stone-200 bg-stone-50 p-3"
              >
                <div className="text-xs font-semibold text-stone-800 uppercase tracking-wide">
                  {r.applicant_profile}
                </div>
                <div className="mt-1 text-sm text-stone-700">{r.route}</div>
                {r.note && (
                  <div className="mt-1 text-xs text-stone-500 italic leading-snug">{r.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <div className="mt-6 pt-4 border-t border-stone-200 flex flex-wrap items-center gap-2 text-[11px] text-stone-500">
          <BookOpen className="w-3 h-3" /> Sources:
          {sources.map((s) => (
            <a
              key={s.url || s.label}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#4C1D95]"
            >
              {s.label}
            </a>
          ))}
        </div>
      )}
    </Card>
  );
}
